import KpiChart from "@app/components/kpi-chart";
import { environment } from "@app/relay/relay-environment";
import { Fragment, Suspense, useCallback, useEffect, useState } from "react";
import {
  fetchQuery,
  graphql,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";

import { useQueueLock } from "@app/components/account-queue/use-queue-lock";

import type { stripeConnectorPage_Query } from "@app/queries/stripeConnectorPage_Query.graphql";
import type { stripeConnectorPage_SyncMutation } from "@app/queries/stripeConnectorPage_SyncMutation.graphql";
import type { stripeConnectorPage_ExportMutation } from "@app/queries/stripeConnectorPage_ExportMutation.graphql";
import type { stripeConnectorPage_DrillQuery } from "@app/queries/stripeConnectorPage_DrillQuery.graphql";
import type { stripeConnectorPage_DisconnectMutation } from "@app/queries/stripeConnectorPage_DisconnectMutation.graphql";
import type { stripeConnectorPage_KeyConnectMutation } from "@app/queries/stripeConnectorPage_KeyConnectMutation.graphql";

const healthQuery = graphql`
  query stripeConnectorPage_Query {
    stripeConnectionStatus {
      connected
      businessName
      connectUrl
      keyPresent
    }
    stripeHealth {
      keyPresent
      runs {
        mode
        status
        startedAt
        finishedAt
        durationMs
        error
        rowsProcessed
        currentObject
        dataFrom
        dataTo
        rows {
          objectType
          count
        }
      }
      objects {
        objectType
        count
        earliest
        latest
        highWaterCreated
        backfillComplete
        backfillInProgress
        lastStatus
        lastError
        lastRows
      }
      coverage {
        month
        invoices
        charges
        newSubs
      }
      reconciliation {
        month
        stripe
        bank
        diff
      }
      integrity {
        orphanCharges
        subsMissingPrice
        customersWithoutSubscription
        stubCustomers
        unknownCountry
      }
      tierCoverage {
        known
        unknown
        pct
      }
      sanity {
        mrr {
          month
          value
        }
        volume {
          month
          value
        }
        newCustomers {
          month
          value
        }
        churnedLogos {
          month
          value
        }
        activeLogos {
          month
          value
        }
      }
      anomalies {
        metric
        month
        note
      }
    }
  }
`;

const syncMutation = graphql`
  mutation stripeConnectorPage_SyncMutation(
    $mode: String
    $objectTypes: [String!]
  ) {
    stripeSyncStart(mode: $mode, objectTypes: $objectTypes) {
      enqueued
      mode
      errors {
        message
      }
    }
  }
`;

const exportMutation = graphql`
  mutation stripeConnectorPage_ExportMutation {
    stripeExportStart {
      enqueued
      dir
      errors {
        message
      }
    }
  }
`;

const disconnectMutation = graphql`
  mutation stripeConnectorPage_DisconnectMutation {
    stripeConnectionDisconnect {
      status {
        connected
      }
      errors {
        message
      }
    }
  }
`;

const keyConnectMutation = graphql`
  mutation stripeConnectorPage_KeyConnectMutation($apiKey: String!) {
    stripeKeyConnect(apiKey: $apiKey) {
      status {
        connected
        keyPresent
      }
      errors {
        message
      }
    }
  }
`;

type Health = stripeConnectorPage_Query["response"]["stripeHealth"];
type ConnectionStatus =
  stripeConnectorPage_Query["response"]["stripeConnectionStatus"];

// Stripe redirects back to the connector page with ?stripe=cancelled (user
// declined) or ?stripe=wrong_mode (live/test mismatch) after a failed connect.
const CALLBACK_NOTICES: Record<string, string> = {
  cancelled: "Stripe connection was cancelled — nothing changed.",
  wrong_mode:
    "That Stripe account is in a different mode (live vs test) than this app — switch modes in Stripe and reconnect.",
};

export default function StripeConnectorPage() {
  // Fully decoupled from Suspense: hold the data in local state and refresh it
  // with fetchQuery (initial load + polling). The component never suspends after
  // mount, so the route's loading fallback never flashes while polling.
  const [health, setHealth] = useState<Health | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [commitSync, syncing] =
    useMutation<stripeConnectorPage_SyncMutation>(syncMutation);
  const [commitExport, exporting] =
    useMutation<stripeConnectorPage_ExportMutation>(exportMutation);
  const [commitDisconnect, disconnecting] =
    useMutation<stripeConnectorPage_DisconnectMutation>(disconnectMutation);
  const [commitKeyConnect, keyConnecting] =
    useMutation<stripeConnectorPage_KeyConnectMutation>(keyConnectMutation);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  // Surface the ?stripe=cancelled / ?stripe=wrong_mode callback flags once.
  const callbackNotice =
    CALLBACK_NOTICES[
      new URLSearchParams(window.location.search).get("stripe") ?? ""
    ] ?? null;
  const [drill, setDrill] = useState<{
    objectType: string;
    month: string;
  } | null>(null);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    fetchQuery<stripeConnectorPage_Query>(
      environment,
      healthQuery,
      {},
      { fetchPolicy: "network-only" }
    ).subscribe({
      next: (response) => {
        setHealth(response.stripeHealth);
        setConnection(response.stripeConnectionStatus);
      },
      complete: () => setIsRefreshing(false),
      error: () => setIsRefreshing(false),
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const running = health?.runs.some((run) => run.status === "running") ?? false;

  // While a sync is in flight, poll so the runs list and counts update live.
  useEffect(() => {
    if (!running) return;
    const id = setInterval(refresh, 3000);
    return () => clearInterval(id);
  }, [running, refresh]);

  // Tick every second so the elapsed/rate readout advances smoothly between polls.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  const runSync = (mode: string, objectTypes?: string[]) =>
    commitSync({
      variables: { mode, objectTypes: objectTypes ?? null },
      onCompleted: () => {
        setTimeout(refresh, 400);
      },
    });

  const disconnect = () =>
    commitDisconnect({
      variables: {},
      onCompleted: () => refresh(),
    });

  const connectWithKey = (apiKey: string, onError: (msg: string) => void) =>
    commitKeyConnect({
      variables: { apiKey },
      onCompleted: (response) => {
        const errors = response.stripeKeyConnect?.errors ?? [];
        if (errors.length > 0) {
          onError(errors[0].message);
          return;
        }
        setTimeout(refresh, 400);
      },
    });

  const runExport = () =>
    commitExport({
      variables: {},
      onCompleted: (response) => {
        const result = response.stripeExportStart;
        setExportNotice(
          result?.enqueued
            ? `Backup export started → ${result.dir}. The folder fills as the COPY streams; it's done when stripe_invoices.jsonl stops growing.`
            : result?.errors[0]?.message ?? "Export could not start."
        );
      },
    });

  if (!health) {
    return (
      <div className="p-6 text-gray-500">Loading the Stripe connector…</div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Stripe connector</h1>
          <p className="text-sm text-gray-500">
            Operate the ingest, prove the data is complete, eyeball the numbers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`text-xs text-gray-400 transition-opacity ${
              isRefreshing ? "opacity-100" : "opacity-0"
            }`}
          >
            updating…
          </span>
          <KeyBadge present={health.keyPresent} />
        </div>
      </div>

      <ConnectPanel
        status={connection}
        callbackNotice={callbackNotice}
        disconnecting={disconnecting}
        keyConnecting={keyConnecting}
        onDisconnect={disconnect}
        onConnectWithKey={connectWithKey}
      />

      {health.runs.find((run) => run.status === "running") && (
        <ActiveRun
          run={health.runs.find((run) => run.status === "running")!}
          now={now}
        />
      )}

      <IngestControl
        objects={health.objects}
        runs={health.runs}
        syncing={syncing}
        exporting={exporting}
        exportNotice={exportNotice}
        latestErrored={health.runs[0]?.status === "error"}
        onSync={runSync}
        onExport={runExport}
        onRefresh={refresh}
      />

      <Quality health={health} />

      <Coverage coverage={health.coverage} onDrill={setDrill} />

      <Reconciliation reconciliation={health.reconciliation} />

      <Sanity sanity={health.sanity} anomalies={health.anomalies} />

      {drill && (
        <Suspense fallback={null}>
          <DrillDown
            objectType={drill.objectType}
            month={drill.month}
            onClose={() => setDrill(null)}
          />
        </Suspense>
      )}
    </div>
  );
}

function KeyBadge({ present }: { present: boolean }) {
  return (
    <span
      className={`text-xs px-2.5 py-1 rounded-full font-medium ${
        present ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
      }`}
    >
      {present ? "API key configured" : "No API key — sync dormant"}
    </span>
  );
}

// The connect experience: hosted Stripe Connect OAuth (canonical) with a
// server-validated restricted-key fallback. Renders the connected identity +
// Disconnect when bound, the Connect button + key affordance otherwise.
function ConnectPanel({
  status,
  callbackNotice,
  disconnecting,
  keyConnecting,
  onDisconnect,
  onConnectWithKey,
}: {
  status: ConnectionStatus | null;
  callbackNotice: string | null;
  disconnecting: boolean;
  keyConnecting: boolean;
  onDisconnect: () => void;
  onConnectWithKey: (apiKey: string, onError: (msg: string) => void) => void;
}) {
  const [showKeyEntry, setShowKeyEntry] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [keyError, setKeyError] = useState<string | null>(null);

  if (!status) return null;

  const submitKey = () => {
    if (!apiKey.trim() || keyConnecting) return;
    setKeyError(null);
    onConnectWithKey(apiKey.trim(), setKeyError);
  };

  return (
    <section className="rounded border bg-white p-4 space-y-3">
      {callbackNotice && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-800">
          {callbackNotice}
        </div>
      )}

      {status.connected ? (
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-medium text-gray-900">
              Connected to {status.businessName ?? "your Stripe account"}
            </div>
            <div className="text-xs text-gray-500">
              Reading via Stripe Connect. Syncs run automatically.
            </div>
          </div>
          <button
            type="button"
            disabled={disconnecting}
            onClick={onDisconnect}
            className="rounded border border-gray-300 text-gray-700 px-3 py-1.5 text-sm font-medium disabled:opacity-50 hover:bg-gray-50"
          >
            Disconnect
          </button>
        </div>
      ) : status.keyPresent ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-700">
            Connected with a restricted API key.
            {status.connectUrl
              ? " Prefer the hosted flow? Connect with Stripe below."
              : ""}
          </div>
          {status.connectUrl && (
            <a
              href={status.connectUrl}
              className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white"
            >
              Connect with Stripe
            </a>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {status.connectUrl && (
              <a
                href={status.connectUrl}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white font-medium"
              >
                Connect Stripe
              </a>
            )}
            <button
              type="button"
              onClick={() => setShowKeyEntry((v) => !v)}
              className="text-sm text-gray-500 hover:text-gray-900 underline"
            >
              {status.connectUrl
                ? "or connect with a restricted key"
                : "Connect with a restricted key"}
            </button>
          </div>

          {showKeyEntry && (
            <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs text-gray-500">
                Paste a Stripe <strong>restricted read-only</strong> key (starts
                with <code>rk_</code>). It is validated and stored encrypted.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="password"
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  placeholder="rk_live_…"
                  className="flex-1 min-w-[16rem] rounded border px-2 py-1.5 text-sm font-mono"
                />
                <button
                  type="button"
                  disabled={!apiKey.trim() || keyConnecting}
                  onClick={submitKey}
                  className="rounded bg-gray-900 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  {keyConnecting ? "Connecting…" : "Connect key"}
                </button>
              </div>
              {keyError && <div className="text-xs text-red-600">{keyError}</div>}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ActiveRun({ run, now }: { run: Health["runs"][number]; now: number }) {
  const startedMs = run.startedAt ? Date.parse(run.startedAt) : now;
  const elapsedSec = Math.max(1, (now - startedMs) / 1000);
  const processed = run.rowsProcessed;
  const rate = processed / elapsedSec;

  // ETA per year of history: how long it's taking to cover a year of created-dates
  // at the current pace, once we've covered enough span to estimate.
  let etaPerYear: string | null = null;
  if (run.dataFrom && run.dataTo) {
    const spanDays =
      (Date.parse(run.dataTo) - Date.parse(run.dataFrom)) / 86_400_000;
    if (spanDays >= 14) {
      etaPerYear = formatDuration((elapsedSec * 365) / spanDays);
    }
  }

  return (
    <div className="rounded border border-blue-200 bg-blue-50 p-4 space-y-1">
      <div className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
        <span className="font-medium text-blue-900">
          Syncing{run.currentObject ? ` ${run.currentObject}` : ""}…
        </span>
      </div>
      <div className="text-sm text-blue-800 flex flex-wrap gap-x-5 gap-y-0.5">
        <span>
          <span className="tabular-nums font-medium">
            {processed.toLocaleString()}
          </span>{" "}
          records
        </span>
        <span>{formatDuration(elapsedSec)} elapsed</span>
        <span>
          <span className="tabular-nums">
            {rate.toFixed(rate >= 10 ? 0 : 1)}
          </span>
          /s
        </span>
        {run.dataFrom && run.dataTo && (
          <span>
            covering {run.dataFrom.slice(0, 10)} → {run.dataTo.slice(0, 10)}
          </span>
        )}
        {etaPerYear && (
          <span className="font-medium">~{etaPerYear} per year of history</span>
        )}
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const MODES = [
  { mode: "backfill", label: "Resume backfill" },
  { mode: "incremental", label: "Refresh recent" },
  { mode: "full", label: "Full re-sync" },
];

function IngestControl({
  objects,
  runs,
  syncing,
  exporting,
  exportNotice,
  latestErrored,
  onSync,
  onExport,
  onRefresh,
}: {
  objects: Health["objects"];
  runs: Health["runs"];
  syncing: boolean;
  exporting: boolean;
  exportNotice: string | null;
  latestErrored: boolean;
  onSync: (mode: string, objectTypes?: string[]) => void;
  onExport: () => void;
  onRefresh: () => void;
}) {
  // A Stripe sync already on the queue makes another pointless — the second
  // could only wait behind the first and redo it.
  const { isQueued, lockedTitle } = useQueueLock();
  const queued = isQueued("STRIPE_SYNC");

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            disabled={syncing || queued}
            onClick={() => onSync(m.mode)}
            title={queued ? lockedTitle : undefined}
            className="rounded bg-gray-900 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            {queued ? `${m.label} — queued` : m.label}
          </button>
        ))}
        <button
          type="button"
          disabled={exporting}
          onClick={onExport}
          className="rounded border border-gray-300 text-gray-700 px-3 py-1.5 text-sm font-medium disabled:opacity-50 hover:bg-gray-50"
        >
          Export backup
        </button>
        <button
          type="button"
          onClick={onRefresh}
          className="text-sm text-gray-500 hover:text-gray-900 px-2"
        >
          Refresh
        </button>
      </div>

      {exportNotice && (
        <div className="rounded border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-600">
          {exportNotice}
        </div>
      )}

      <p className="text-xs text-gray-400">
        Global buttons act on every object type. Each card's own buttons sync
        just that type — handy when one entity is lagging.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {objects.map((o) => {
          // Only surface an error on the card when the most recent run failed —
          // a stale cursor error from an older run shouldn't show while a sync
          // is running or after a later run succeeded.
          const showError = latestErrored && o.lastStatus === "error";
          const backfillState = o.backfillComplete
            ? "backfilled"
            : o.backfillInProgress
            ? "backfill in progress"
            : "backfill pending";
          return (
            <div
              key={o.objectType}
              className="bg-white rounded border p-3 space-y-2"
            >
              <div>
                <div className="flex items-baseline justify-between">
                  <span className="font-medium capitalize">{o.objectType}</span>
                  <span className="tabular-nums text-gray-900">
                    {o.count.toLocaleString()}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {o.earliest && o.latest
                    ? `${shortDate(o.earliest)} → ${shortDate(o.latest)}`
                    : "no timestamps"}
                </div>
                <div className="text-xs flex items-center gap-1.5">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      showError
                        ? "bg-red-500"
                        : o.lastStatus === "success"
                        ? "bg-green-500"
                        : "bg-gray-300"
                    }`}
                  />
                  <span className="text-gray-500">
                    {backfillState}
                    {o.lastRows != null ? ` · ${o.lastRows} last run` : ""}
                  </span>
                </div>
                {showError && o.lastError && (
                  <div
                    className="text-xs text-red-600 truncate"
                    title={o.lastError}
                  >
                    {o.lastError}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={syncing}
                  onClick={() => onSync("backfill", [o.objectType])}
                  className="rounded bg-gray-900 text-white px-2 py-1 text-xs font-medium disabled:opacity-50"
                >
                  Resume backfill
                </button>
                <button
                  type="button"
                  disabled={syncing}
                  onClick={() => onSync("full", [o.objectType])}
                  className="rounded border border-gray-300 text-gray-700 px-2 py-1 text-xs font-medium disabled:opacity-50 hover:bg-gray-50"
                >
                  Resync all
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <RunHistory runs={runs} />
    </section>
  );
}

const STATUS_COLOR: Record<string, string> = {
  success: "text-green-600",
  error: "text-red-600",
  interrupted: "text-amber-600",
  running: "text-blue-600",
};

function RunHistory({ runs }: { runs: Health["runs"] }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  if (runs.length === 0)
    return <p className="text-sm text-gray-400">No sync runs yet.</p>;

  return (
    <div className="bg-white rounded border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Started</th>
            <th className="text-left px-3 py-2 font-medium">Mode</th>
            <th className="text-left px-3 py-2 font-medium">Status</th>
            <th className="text-right px-3 py-2 font-medium">Duration</th>
            <th className="text-left px-3 py-2 font-medium">Rows</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {runs.map((run, i) => {
            const hasError = !!run.error;
            const isOpen = expanded === i;
            return (
              <Fragment key={i}>
                <tr
                  className={hasError ? "cursor-pointer hover:bg-gray-50" : ""}
                  onClick={() => hasError && setExpanded(isOpen ? null : i)}
                >
                  <td className="px-3 py-1.5 text-gray-500">
                    {run.startedAt ? shortDate(run.startedAt) : "—"}
                  </td>
                  <td className="px-3 py-1.5">{run.mode}</td>
                  <td className="px-3 py-1.5">
                    <span
                      className={STATUS_COLOR[run.status] ?? "text-gray-500"}
                    >
                      {run.status}
                    </span>
                    {hasError && (
                      <span className="text-gray-400 ml-1.5 text-xs">
                        {isOpen ? "▾ hide" : "▸ details"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                    {run.durationMs != null ? `${run.durationMs}ms` : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-gray-500 truncate max-w-xs">
                    {run.rows
                      .map((r) => `${r.objectType} ${r.count}`)
                      .join(", ") || "—"}
                  </td>
                </tr>
                {hasError && isOpen && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-2 bg-red-50 text-red-700 text-xs font-mono whitespace-pre-wrap"
                    >
                      {run.error}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Quality({ health }: { health: Health }) {
  const integrity = health.integrity;
  const tier = health.tierCoverage;
  const stats = [
    {
      label: "Tier-mapped subs",
      value: `${(tier.pct * 100).toFixed(0)}%`,
      warn: tier.pct < 0.95,
    },
    {
      label: "Subs missing price",
      value: integrity.subsMissingPrice,
      warn: integrity.subsMissingPrice > 0,
    },
    {
      label: "Orphan charges",
      value: integrity.orphanCharges,
      warn: integrity.orphanCharges > 0,
    },
    {
      label: "Customers w/o sub",
      value: integrity.customersWithoutSubscription,
      warn: false,
    },
    { label: "Stub customers", value: integrity.stubCustomers, warn: false },
    { label: "Unknown country", value: integrity.unknownCountry, warn: false },
  ];
  return (
    <section className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {stats.map((s) => (
        <div
          key={s.label}
          className={`rounded border p-3 ${
            s.warn ? "bg-amber-50 border-amber-200" : "bg-white"
          }`}
        >
          <div className="text-lg font-semibold tabular-nums">{s.value}</div>
          <div className="text-xs text-gray-500">{s.label}</div>
        </div>
      ))}
    </section>
  );
}

function Coverage({
  coverage,
  onDrill,
}: {
  coverage: Health["coverage"];
  onDrill: (d: { objectType: string; month: string }) => void;
}) {
  if (coverage.length === 0) return null;
  const rows = [
    { key: "invoices" as const, label: "Invoices", type: "invoices" },
    { key: "charges" as const, label: "Charges", type: "charges" },
    { key: "newSubs" as const, label: "New subs", type: "subscriptions" },
  ];
  return (
    <section className="space-y-2">
      <h2 className="font-bold">Monthly coverage</h2>
      <p className="text-xs text-gray-500">
        A red cell is a month with zero of that object — a likely gap. Click a
        cell to inspect the raw objects.
      </p>
      <div className="overflow-x-auto bg-white rounded border p-3">
        <table className="text-xs border-separate" style={{ borderSpacing: 2 }}>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td className="pr-3 whitespace-nowrap sticky left-0 bg-white font-medium">
                  {row.label}
                </td>
                {coverage.map((c) => {
                  const n = c[row.key];
                  return (
                    <td
                      key={c.month}
                      onClick={() =>
                        onDrill({ objectType: row.type, month: c.month })
                      }
                      title={`${c.month}: ${n} ${row.label.toLowerCase()}`}
                      className={`rounded-sm cursor-pointer ${
                        n > 0
                          ? "bg-green-200 hover:bg-green-300"
                          : "bg-red-300 hover:bg-red-400"
                      }`}
                      style={{ width: 16, height: 16 }}
                    />
                  );
                })}
              </tr>
            ))}
            <tr>
              <td />
              {coverage.map((c, i) => (
                <td
                  key={c.month}
                  className="text-gray-400 align-top"
                  style={{ writingMode: "vertical-rl", fontSize: 8 }}
                >
                  {i % 3 === 0 ? c.month : ""}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Reconciliation({
  reconciliation,
}: {
  reconciliation: Health["reconciliation"];
}) {
  if (reconciliation.length === 0) return null;
  return (
    <section className="space-y-2">
      <h2 className="font-bold">Stripe ↔ bank reconciliation</h2>
      <p className="text-xs text-gray-500">
        Paid Stripe invoices vs bank deposits per month. Large gaps mean the two
        revenue pipelines disagree.
      </p>
      <KpiChart
        title="Paid invoices vs bank inflows"
        format="money"
        onMonthClick={() => {}}
        series={[
          {
            name: "Stripe",
            type: "bar",
            color: "#635bff",
            points: reconciliation.map((r) => ({
              month: r.month,
              value: r.stripe,
            })),
          },
          {
            name: "Bank",
            type: "bar",
            color: "#16a34a",
            points: reconciliation.map((r) => ({
              month: r.month,
              value: r.bank,
            })),
          },
          {
            name: "Diff",
            type: "line",
            color: "#dc2626",
            points: reconciliation.map((r) => ({
              month: r.month,
              value: r.diff,
            })),
          },
        ]}
      />
    </section>
  );
}

function Sanity({
  sanity,
  anomalies,
}: {
  sanity: Health["sanity"];
  anomalies: Health["anomalies"];
}) {
  const charts: {
    title: string;
    points: Health["sanity"]["mrr"];
    type: "line" | "bar";
    format: "money" | "number";
    color: string;
  }[] = [
    {
      title: "MRR",
      points: sanity.mrr,
      type: "line",
      format: "money",
      color: "#2563eb",
    },
    {
      title: "Billed volume",
      points: sanity.volume,
      type: "bar",
      format: "money",
      color: "#635bff",
    },
    {
      title: "New customers / mo",
      points: sanity.newCustomers,
      type: "bar",
      format: "number",
      color: "#16a34a",
    },
    {
      title: "Churned logos / mo",
      points: sanity.churnedLogos,
      type: "bar",
      format: "number",
      color: "#dc2626",
    },
    {
      title: "Active logos",
      points: sanity.activeLogos,
      type: "line",
      format: "number",
      color: "#0891b2",
    },
  ];
  return (
    <section className="space-y-2">
      <h2 className="font-bold">Gut check</h2>
      {anomalies.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-1">
          <span className="font-medium text-amber-800">Anomalies</span>
          {anomalies.map((a, i) => (
            <div key={i} className="text-amber-700 text-xs">
              {a.month}: {a.note}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {charts.map((c) =>
          c.points.length > 0 ? (
            <KpiChart
              key={c.title}
              title={c.title}
              format={c.format}
              onMonthClick={() => {}}
              series={[
                {
                  name: c.title,
                  type: c.type,
                  color: c.color,
                  points: c.points,
                },
              ]}
            />
          ) : null
        )}
      </div>
    </section>
  );
}

const drillQuery = graphql`
  query stripeConnectorPage_DrillQuery($objectType: String!, $month: String!) {
    stripeRawObjects(objectType: $objectType, month: $month) {
      stripeId
      created
      status
      amount
      label
    }
  }
`;

function DrillDown({
  objectType,
  month,
  onClose,
}: {
  objectType: string;
  month: string;
  onClose: () => void;
}) {
  const data = useLazyLoadQuery<stripeConnectorPage_DrillQuery>(drillQuery, {
    objectType,
    month,
  });
  const rows = data.stripeRawObjects;
  return (
    <div className="fixed inset-y-0 right-0 w-[28rem] bg-white border-l shadow-xl overflow-y-auto z-50">
      <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
        <div>
          <div className="font-bold capitalize">{objectType}</div>
          <div className="text-xs text-gray-500">
            {month} · {rows.length} objects
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-900 text-lg"
        >
          ×
        </button>
      </div>
      <table className="w-full text-xs">
        <tbody className="divide-y">
          {rows.map((r) => (
            <tr key={r.stripeId}>
              <td className="px-3 py-1.5 font-mono text-gray-500">
                {r.stripeId}
              </td>
              <td className="px-3 py-1.5">{r.label}</td>
              <td className="px-3 py-1.5">{r.status}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {r.amount != null
                  ? r.amount.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })
                  : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function shortDate(iso: string): string {
  return iso.slice(0, 10);
}
